/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect } from 'react';
import moment from 'moment';
import {
  Button, Select, DatePicker, Input, Form, notification,
} from 'antd';
import {hooks, selectors} from 'farmx-redux-core';
import isEqual from 'react-fast-compare';
import { useSelector } from 'react-redux';
import { displayConvertedDepth } from './depthUnitHelper';
import { LABEL_CELSIUS } from '../constants';
import {
  createThresholdConfig,
  updateThresholdConfig
} from './settingsHelper';

const { selectUserDepthFormat, selectUserPressureFormat, selectUserTemperatureFormat } = selectors;

// This will be updated in the future
const defaultRootZoneDepth = 6;
const allWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const irrigationMaxDuration = 90; // Valve default max duration 
const sprinklerMaxDuration = 120; // Valve default max duration 

// To maintain the existing selection on edit mode
export const autonomousTypes = {
  unknown: { actionType: 'unknown', subType: 'unknown'},
  irrigation: {
    actionType: 'irrigation',
    subType: 'autonomous_irrigation'
  },
  sprinkler: {
    actionType: 'sprinkler',
    subType: 'autonomous_sprinkler_irrigation'
  },
  cooling: {
    actionType: 'sprinkler',
    subType: 'autonomous_sprinkler_cooling'
  },
  time_shift_irrigation: {
    actionType: 'irrigation',
    subType: 'time_shift_autonomous_irrigation'
  },
  time_shift_sprinkler_irrigation: {
    actionType: 'sprinkler',
    subType: 'time_shift_autonomous_sprinkler_irrigation'
  }
};

export const autonomouseKeys = {
  'unknown/unknown': 'unknown',
  'irrigation/autonomous_irrigation': 'irrigation',
  'sprinkler/autonomous_sprinkler_irrigation': 'sprinkler',
  'sprinkler/autonomous_sprinkler_cooling': 'cooling', // for rendering
  'irrigation/time_shift_autonomous_irrigation': 'time_shift_irrigation',
  'sprinkler/time_shift_autonomous_sprinkler_irrigation': 'time_shift_sprinkler_irrigation',
};

export const useRenderReusableControls = (t: any) => {
  const renderButton = (name: any, type?: any, htmlType?: any,
    onClick?: any, disabled?: any, block?: any, loading?: any,
    className?: any) => (
      <Button
        type={type || undefined}
        htmlType={htmlType || undefined}
        onClick={onClick}
        disabled={disabled}
        block={!block}
        loading={loading}
        className={className}
      >
        {t(name)}
      </Button>
  );

  const renderSelect = (options: any, placeholder?: any,
    value?: any, onChange?: any, disabled?: any, loading?: any, className?: any) => (
      <Select
        placeholder={t(placeholder)}
        {...(value !== undefined ? { value } : {})}
        options={options}
        onChange={onChange}
        disabled={disabled}
        className={className}
        loading={loading}
        getPopupContainer={(triggerNode: any) => triggerNode.parentNode}
      />
  );

  const renderDatePicker = (dateValue: any, unitsTime?: any, onChange?: any) => (
    <DatePicker
      showTime
      showNow={false}
      format={unitsTime
        ? 'YYYY-MM-DD hh:mm:ss a' : 'YYYY-MM-DD HH:mm:ss'}
      defaultValue={dateValue}
      use12Hours={unitsTime}
      onOk={undefined}
      className="form-item-width-100"
      getPopupContainer={() => document.body}
      onChange={onChange}
      placeholder={t('Select Date')}
    />
  );

  const renderInputFormItem = (name: string, label: any, unitLabel: string,
    className?: any, rule?: any, placeholder?: any) => (
      <Form.Item
        name={name}
        label={label}
        className={className || 'font-bold flex-row-center'}
        rules={rule}
      >
        <Input
          placeholder={t(placeholder)}
          suffix={t(unitLabel)}
        />
      </Form.Item>
  );

  return {
    renderButton, renderSelect, renderDatePicker, renderInputFormItem,
  };
};

export const generateArray = (num: number) => [...Array(num).keys()].map((x) => x + 1);

export const generateSensorDepthOptions = (depthCount: number,
  sensorStatus: any, getUserUnits: any, depthFormat: any) => generateArray(depthCount || 0)
    ?.map((d: number) => {
      const depthValue = sensorStatus?.[`depth${d}`] || (d * defaultRootZoneDepth);
      const convertedValve = displayConvertedDepth(depthValue, getUserUnits, depthFormat);
      const [value] = convertedValve?.split(' ');
      return { value, label: convertedValve };
    });

export const getDefaultTimes = (sDate?: any, eDate?: any, sTime?: object, eTime?: object) => {
  const startTime = moment(sDate).set({ 
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
    ...(sTime || {})
  });
  const endTime = moment(eDate).set({ 
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
    ...(eTime || {})
  });

  return { startTime, endTime };
};

export const useUpdateFormFields = (autoFillValues: any, form: any, now: any, endDate: any,
  autonomousMethod: any, setUpdatedInitialValues: any, setSelectedBlock: any,
  setSelectedSensor: any, setAutonomousMethod: any, prefix?: string, isEdit?: boolean) => {
  const getUserUnits = hooks.useUnits();
  const defaultTemperatureUnit = 'degrees_celsius';
  const { startTime, endTime } = getDefaultTimes(undefined, undefined, { hour: 6 }, { hour: 7 });

  useEffect(() => {
    if (autoFillValues) {
      const {
        block_id, action_type: actionType,
        action_subtype: actionSubType,
        schedule,
        controls,
        reference_sensor_id: id,
        reference_sensor_type: type,
        reference_sensor_identifier: identifier
      } = autoFillValues;
      const {
        starting_time_window: startTimeWindow,
        ending_time_window: endTimeWindow,
      } = schedule || {};

      const {
        starting_temperature: startingTemperature,
        stopping_temperature: stoppingTemperature,
        source_starting_time_window: sourceStartingTimeWindow,
        source_ending_time_window: sourceEndingTimeWindow,
      } = controls || {};
      const key = autonomouseKeys?.[`${actionType}/${actionSubType}`];

      const {value: covertedRefillDepth } = getUserUnits(Number(controls?.refill_point_depth),
        'inches', 'depth', {});
      const {value: covertedStoppingDepth } = getUserUnits(Number(controls?.stopping_point_depth),
        'inches', 'depth', {});

      const {value: startTemperature } = getUserUnits(Number(startingTemperature || 0),
        defaultTemperatureUnit, 'temperature', {});

      const {value: stopTemperature } = getUserUnits(Number(stoppingTemperature || 0),
        defaultTemperatureUnit, 'temperature', {});

      const updatedObj = {
        ...autoFillValues,
        block: block_id,
        action_type: key,
        ...({
          ...controls,
          starting_temperature: startTemperature,
          stopping_temperature: stopTemperature,
          refill_point_depth: Math.round(covertedRefillDepth),
          stopping_point_depth: Math.round(covertedStoppingDepth)
        }),
        ...({
          ...autoFillValues?.schedule,
          starting_time_window: moment(startTimeWindow),
          ending_time_window: moment(endTimeWindow),
        }),
        source_starting_time_window: moment(sourceStartingTimeWindow),
        source_ending_time_window: moment(sourceEndingTimeWindow),
      };
      setUpdatedInitialValues(updatedObj);
      setSelectedBlock(block_id);
      setSelectedSensor({
        reference_sensor_id: id,
        reference_sensor_type: type,
        reference_sensor_identifier: identifier
      });
      setAutonomousMethod(key);
      form.setFieldsValue({ action_type: key });
      form.setFieldsValue({ sensor: { id, type, identifier} });
      form.setFieldsValue(updatedObj);
    } else {
      form.setFieldsValue({ starting_time_window: now });
      form.setFieldsValue({ ending_time_window: endDate });
      form.setFieldsValue({ action_type: autonomousMethod });
      form.setFieldsValue({ extended_time: 0 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillValues, form]);

  // To set defult time when autonomous method changes
  useEffect(() => {
    if (isEdit) return;

    if (autonomousMethod === 'time_shift_irrigation' || autonomousMethod === 'time_shift_sprinkler_irrigation') {
      form.setFieldsValue({ starting_time_window: now });
      form.setFieldsValue({ ending_time_window: endDate });  
      form.setFieldsValue({ [`${prefix}starting_time_window`]: startTime });
      form.setFieldsValue({ [`${prefix}ending_time_window`]: endTime });  
    } else {
      form.setFieldsValue({ starting_time_window: now });
      form.setFieldsValue({ ending_time_window: endDate });  
    }
  }, [autonomousMethod])
};

export const useUpdateGlobalSettingsFormFields = (autoFillValues: any, form: any,
  setSelectedSensor: any, setLocalBlocks?: any, setUpdatedInitialValues?: any, prefix?: string) => {
  const getUserUnits = hooks.useUnits();
  const pressureFormat = useSelector(selectUserPressureFormat);

  useEffect(() => {
    if (autoFillValues) {
      const {
        reservoir_threshold, id: editId,
      } = autoFillValues;

      const {
        blocks,
        pressure_low_threshold: pressureLowThreshold,
        pressure_high_threshold: pressureHighThreshold,
        pressure_reference_sensor_id: id,
        pressure_reference_sensor_type: type,
        pressure_reference_sensor_identifier: identifier
      } = reservoir_threshold || {};

      const fromUnit = 'pounds_per_square_inch';

      const {value: pressureHigh } = getUserUnits(Number(pressureHighThreshold || 0),
        fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);
      const {value: pressureLow } = getUserUnits(Number(pressureLowThreshold || 0),
        fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);
    
      const updatedObj = {
        ...autoFillValues,
        id: editId,
        irrigation_pressure_low: Number(pressureLow),
        irrigation_pressure_high: Number(pressureHigh),
        blocks,
      };
      setSelectedSensor({
        reference_sensor_id: id,
        reference_sensor_type: type,
        reference_sensor_identifier: identifier
      });
      setLocalBlocks(blocks);
      form.setFieldsValue({ id: editId });
      form.setFieldsValue({ sensor: { id, type, identifier} });
      form.setFieldsValue(updatedObj);
      setUpdatedInitialValues(updatedObj);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillValues, form]);
};

// Below funtion is to handle the Form submit of create and edit flow
export const useHandlers = (updatedInitialValues: any, isEdit: any,
  onUpdate: any, prefix?: string) => {
  const getUserUnits = hooks.useUnits();
  const depthFormat = useSelector(selectUserDepthFormat);
  const temperatureFormat = useSelector(selectUserTemperatureFormat);
  const temperatureToUnit = 'degrees_celsius';
  const pressureFormat = useSelector(selectUserPressureFormat);
  const pressureToUnit = 'pounds_per_square_inch';

  const onFinish = (fieldValues: any) => {
    const {
      action_type, block, sensor, schedule,
      refill_point, refill_point_depth, stopping_point, stopping_point_depth,
      extended_time, starting_temperature, stopping_temperature, depth,
      irrigation, irrigationPressure, irrigation_pressure_high, irrigation_pressure_low,
    } = fieldValues || {};

    const sensorControl = {
      reference_sensor_id: sensor?.id,
      reference_sensor_identifier: sensor?.identifier,
      reference_sensor_type: sensor?.type
    };

    // Convert the depth values always in "Inches" before sending to backend API
    const {value: covertedRefillPoint } = getUserUnits(refill_point_depth, depthFormat, undefined, {}, 'inches');
    const {value: covertedStoppingPoint } = getUserUnits(stopping_point_depth, depthFormat, undefined, {}, 'inches');
    const {value: startingTemperature } = getUserUnits(Number(starting_temperature),
      temperatureFormat, undefined, {}, temperatureToUnit);
    const {value: stoppingTemperature } = getUserUnits(Number(stopping_temperature),
      temperatureFormat, undefined, {}, temperatureToUnit);
    const {value: covertedDepth } = getUserUnits(Number(depth), depthFormat, undefined, {}, 'inches');
    const {value: pressureHigh } = getUserUnits(Number(irrigation_pressure_high),
      pressureFormat, undefined, {}, pressureToUnit);
    const {value: pressureLow } = getUserUnits(Number(irrigation_pressure_low),
      pressureFormat, undefined, {}, pressureToUnit);

    const irrigationControls = {
      refill_point: Number(refill_point),
      refill_point_depth: Math.round(covertedRefillPoint),
      stopping_point: Number(stopping_point),
      stopping_point_depth: Math.round(covertedStoppingPoint),
      extended_time: Number(extended_time),
      extended_time_units: updatedInitialValues?.controls?.extended_time_units || 'min',
    };

    const coolingControls = {
      starting_temperature: Number(startingTemperature),
      stopping_temperature: Number(stoppingTemperature),
      temperature_units: updatedInitialValues?.controls?.temperature_units || LABEL_CELSIUS,
    };

    let controls = {};
    const isTimeShift = action_type === "time_shift_irrigation" || action_type === "time_shift_sprinkler_irrigation";
    if (isTimeShift) {
      controls = {
        [`${prefix}reference_sensor_id`]: irrigation?.[`${prefix}reference_sensor_id`],
        [`${prefix}reference_sensor_identifier`]: irrigation
          ?.[`${prefix}reference_sensor_identifier`],
        [`${prefix}reference_sensor_type`]: irrigation?.[`${prefix}reference_sensor_type`],
        [`${prefix}pressure_reference_sensor_id`]: irrigationPressure
          ?.[`${prefix}pressure_reference_sensor_id`],
        [`${prefix}pressure_reference_sensor_identifier`]: irrigationPressure
          ?.[`${prefix}pressure_reference_sensor_identifier`],
        [`${prefix}pressure_reference_sensor_type`]: irrigationPressure
          ?.[`${prefix}pressure_reference_sensor_type`],
        depth: Math.round(covertedDepth),
        irrigation_pressure_high: Number(pressureHigh),
        irrigation_pressure_low: Number(pressureLow),
        [`${prefix}starting_time_window`]: moment(fieldValues
          ?.[`${prefix}starting_time_window`]).toISOString(),
        [`${prefix}ending_time_window`]: moment(fieldValues
          ?.[`${prefix}ending_time_window`]).toISOString(),
      }
    }
    const finalValue = {
      block_id: block,
      action_type: autonomousTypes?.[action_type]?.actionType,
      action_subtype: autonomousTypes?.[action_type]?.subType,
      ...sensorControl,
      ...(!isTimeShift ? { 
        controls: action_type !== 'cooling' ? irrigationControls : coolingControls
      } : { controls }),
      schedule: {
        ...(schedule?.is_recurring ? schedule : {
          is_recurring: false,
          ...(schedule ? { id: schedule?.id } : {}),
          ...schedule,
        }),
        ...(!schedule && { is_recurring: false }),
        starting_time_window: moment(fieldValues?.starting_time_window).toISOString(),
        ending_time_window: moment(fieldValues?.ending_time_window).toISOString(),
      },
    };

    if (onUpdate && !isEdit) onUpdate({ create: finalValue });
    if (onUpdate && isEdit) onUpdate({ edit: finalValue });
  };

  const onGlobalSettingsFinish = (fieldValues: any) => {
    const {
      sensor, blocks, irrigation_pressure_high, irrigation_pressure_low,
      id: editId
    } = fieldValues;
    const { id, type, identifier } = sensor;
    const {value: pressureHigh } = getUserUnits(Number(irrigation_pressure_high),
      pressureFormat, undefined, {}, pressureToUnit);
    const {value: pressureLow } = getUserUnits(Number(irrigation_pressure_low),
      pressureFormat, undefined, {}, pressureToUnit);

    const finalValue = {
      ...(editId ? { id: editId } : {}),
      reservoir_threshold: {
        blocks,
        pressure_low_threshold: Number(pressureLow),
        pressure_high_threshold: Number(pressureHigh),
        pressure_reference_sensor_id: id,
        pressure_reference_sensor_type: type,
        pressure_reference_sensor_identifier: identifier
      }
    }
    if (onUpdate && !isEdit) onUpdate({ create: finalValue });
    if (onUpdate && isEdit) onUpdate({ edit: finalValue });
  }

  return { onFinish, onGlobalSettingsFinish };
};

// Below hook will update the autonomous type state based on the block irrigation control settings
export const useSetActionType = (allConfig: any, selectedBlock: any, form: any,
  setAutonomousMethod: any) => {
  // Filter the config of the selected block
  const filteredConfig = allConfig?.filter((d: any) => d?.block_id === selectedBlock)
    ?.reduce((a: any, o: any) => {
      const obj = {...a};
      obj[o.block_id]= o.configs;
      return obj;
    }, {});

  // To make the autonomous select component read-only
  const selectedConfig = filteredConfig[selectedBlock];
  const isDripIrrigation = selectedConfig?.irrigation?.vwc_irrigation;
  const isSprinklerCooling = selectedConfig?.cooling?.vwc_cooling;
  const isSprinklerIrrigation = selectedConfig?.sprinkler?.vwc_irrigation;

  useEffect(() => {
    if (isDripIrrigation) {
      setAutonomousMethod('irrigation');
      form.setFieldsValue({ action_type: 'irrigation' });
    }
    if (isSprinklerIrrigation) {
      setAutonomousMethod('sprinkler');
      form.setFieldsValue({ action_type: 'sprinkler' });
    }
    if (isSprinklerCooling) {
      setAutonomousMethod('cooling');
      form.setFieldsValue({ action_type: 'cooling' });
    }
  }, [form, isDripIrrigation, isSprinklerCooling, isSprinklerIrrigation, setAutonomousMethod]);

  return { isDripIrrigation, isSprinklerCooling, isSprinklerIrrigation };
};

export const convertToWeekdayObject = (recurringWeekdays: string, currentWeekday: number) => {
  // Get the current weekday as a 0-based index (Sunday = 0)
  const currentWeekdayIndex = currentWeekday % 7; // Adjust for ISO (Mon = 1)

  // Create an object with all days set to false
  const result = Object.fromEntries(allWeekdays.map((day) => [day, false]));

  if (recurringWeekdays?.length) {
    // Split the input string and update the corresponding keys to true
    recurringWeekdays.split(',').forEach((day) => {
      // eslint-disable-next-line no-prototype-builtins
      if (result.hasOwnProperty(day)) {
        result[day] = true;
      }
    });
  } else {
    // Set the current weekday to true
    result[allWeekdays[currentWeekdayIndex]] = true;
  }

  return result;
};

export const getRepeatWeekDaysString = (repeatWeekdays: any) => Object.keys(repeatWeekdays)
  .filter((day) => repeatWeekdays[day])
  .join(',');

export const preparethresholdFormObj = (data: any, getUserUnits: any, pressureFormat: any) => {
  console.log('data', data);
  const [sensor] = data?.filter((d: any) => d?.action_type === 'irrigation'
    && ['pixl_soil', 'aquacheck_soil'].includes(d?.main_threshold?.smp_reference_sensor_type));
  const [sprinklerPressureSensor] = data?.filter((d: any) => d?.action_type === 'sprinkler'
    && d?.main_threshold?.pressure_reference_sensor_type === 'water_pressure');
  const [pressureSensor] = data?.filter((d: any) => d?.action_type === 'irrigation'
    && d?.main_threshold?.pressure_reference_sensor_type === 'water_pressure');

  console.log('sensors', sensor, sprinklerPressureSensor);

  const {
    pressure_low_threshold,
    pressure_high_threshold,
    smp_reference_sensor_id,
    smp_reference_sensor_type,
    pressure_reference_sensor_id,
    pressure_reference_sensor_type,
    smp_reference_sensor_identifier,
    pressure_reference_sensor_identifier,
    max_valve_opening_duration,
    depth,
  } = sensor?.main_threshold || pressureSensor?.main_threshold || {};

  const {value: covertedDepth } = getUserUnits(Number(depth || 0),
    'inches', 'depth', {});

  const {
    pressure_low_threshold: sprinklerPressureLow,
    pressure_high_threshold: sprinklerPressureHigh,
    pressure_reference_sensor_id: sprinklerPressureId,
    pressure_reference_sensor_type: sprinklerPressureType,
    pressure_reference_sensor_identifier: sprinklerPressureIdentifier,
    max_valve_opening_duration: sprinklerMaxValveOpeningDuration,
    smp_reference_sensor_id: sprinklerSmpId,
    smp_reference_sensor_type: sprinklerSmpType,
    smp_reference_sensor_identifier: sprinklerSmpIdentifier,
    depth: sprinklerDepth
  } = sprinklerPressureSensor?.main_threshold || {};

  const {value: covertedSprinklerDepth } = getUserUnits(Number(sprinklerDepth || 0),
  'inches', 'depth', {});
  const fromUnit = 'pounds_per_square_inch';

  const {value: pressureHigh } = getUserUnits(Number(pressure_high_threshold || 0),
    fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);
  const {value: pressureLow } = getUserUnits(Number(pressure_low_threshold || 0),
    fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);
  const {value: spPressureHigh } = getUserUnits(Number(sprinklerPressureHigh || 0),
    fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);
  const {value: spPressureLow } = getUserUnits(Number(sprinklerPressureLow || 0),
    fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);

  const irrigation = {
    reference_sensor_type: smp_reference_sensor_type,
    reference_sensor_id: smp_reference_sensor_id,
    reference_sensor_identifier: smp_reference_sensor_identifier,
  };

  const sprinkler = sprinklerSmpId ? {
    reference_sensor_type: sprinklerSmpType,
    reference_sensor_id: sprinklerSmpId,
    reference_sensor_identifier: sprinklerSmpIdentifier,
  } : undefined

  const irrigationPressure = {
    reference_sensor_type: pressure_reference_sensor_type,
    reference_sensor_id: pressure_reference_sensor_id,
    reference_sensor_identifier: pressure_reference_sensor_identifier,
  };

  const sprinklerPressure = {
    reference_sensor_type: sprinklerPressureType,
    reference_sensor_id: sprinklerPressureId,
    reference_sensor_identifier: sprinklerPressureIdentifier,
  };

  const formObj = {
    irrigation,
    sprinkler,
    depth: Math.round(covertedDepth) || undefined,
    sprinklerDepth: Math.round(covertedSprinklerDepth) || undefined,
    irrigationPressure,
    irrigation_pressure_high: pressureHigh,
    irrigation_pressure_low: pressureLow,
    sprinklerPressure,
    sprinkler_pressure_high: spPressureHigh,
    sprinkler_pressure_low: spPressureLow,
    irrigation_max_valve_opening_duration: max_valve_opening_duration || irrigationMaxDuration,
    sprinkler_max_valve_opening_duration: sprinklerMaxValveOpeningDuration || sprinklerMaxDuration,
  };

  return formObj;
};

export const prepareIrrigationThresholdFormObj = (data: any, prefix: string, getUserUnits: any, pressureFormat: any) => {
  const {
    [`${prefix}reference_sensor_type`]: reference_sensor_type,
    [`${prefix}reference_sensor_id`]: reference_sensor_id,
    [`${prefix}reference_sensor_identifier`]: reference_sensor_identifier,
    [`${prefix}pressure_reference_sensor_type`]: pressure_reference_sensor_type,
    [`${prefix}pressure_reference_sensor_id`]: pressure_reference_sensor_id,
    [`${prefix}pressure_reference_sensor_identifier`]: pressure_reference_sensor_identifier,
    irrigation_pressure_low,
    irrigation_pressure_high,
    depth,
  } = data || {};

  const {value: covertedDepth } = getUserUnits(Number(depth || 0),
    'inches', 'depth', {});

  const fromUnit = 'pounds_per_square_inch';

  const {value: pressureHigh } = getUserUnits(Number(irrigation_pressure_high || 0),
    fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);
  const {value: pressureLow } = getUserUnits(Number(irrigation_pressure_low || 0),
    fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);

  const irrigation = {
    [`${prefix}reference_sensor_type`]: reference_sensor_type,
    [`${prefix}reference_sensor_id`]: reference_sensor_id,
    [`${prefix}reference_sensor_identifier`]: reference_sensor_identifier,
  };

  const irrigationPressure = {
    [`${prefix}pressure_reference_sensor_type`]: pressure_reference_sensor_type,
    [`${prefix}pressure_reference_sensor_id`]: pressure_reference_sensor_id,
    [`${prefix}pressure_reference_sensor_identifier`]: pressure_reference_sensor_identifier,
  };

  const formObj = {
    irrigation,
    depth: Math.round(covertedDepth) || undefined,
    irrigationPressure,
    irrigation_pressure_high: pressureHigh,
    irrigation_pressure_low: pressureLow,
  };

  return formObj;
};

export const getThresholdConfig = (mainThresholds: any, ranchId: any, actionType: string) => {
  const thresholdConfigs = mainThresholds.length?mainThresholds
    .filter((d: any) => (d?.action_type === actionType
    && d?.ranch === ranchId)):undefined;
  const [thresholdConfig] = thresholdConfigs || [undefined];

  return {config: thresholdConfig, isEdit: !!(ranchId && thresholdConfigs?.length) };
};

function getThresholdAPIHandler(request: any, isEdit: any, messageStrObj: any,
  setLoading: any, t: any) {
  if (isEdit) {
    if (setLoading) setLoading(true);
    updateThresholdConfig(request, (success: boolean, response: any) => {
      if (success) {
        notification.success({
          message: t(`Settings ${messageStrObj?.success} successfully`),
          maxCount: 1
        });
      } else {
        notification.error({
          message: t(`Failed to ${messageStrObj?.error} settings`),
          maxCount: 1
        });
      }
      if (setLoading) setLoading(false);
    });
  } else {
    if (setLoading) setLoading(true);
    createThresholdConfig(request, (success: boolean, response: any) => {
      if (success) {
        notification.success({
          message: t(`Settings ${messageStrObj?.success} successfully`),
          maxCount: 1
        });
      } else {
        notification.error({
          message: t(`Failed to ${messageStrObj?.error} settings`),
          maxCount: 1
        });
      }
      if (setLoading) setLoading(false);
    });
  }
}

function getNoteficationMessageStr(isEdit: any) {
  return {
    success: isEdit ? 'updated' : 'created',
    error: isEdit ? 'update' : 'create'
  };
}

export const useThresholdHandlers = (mainThresholds: any, ranchId: any, t: any) => {
  const getUserUnits = hooks.useUnits();
  const depthFormat = useSelector(selectUserDepthFormat);
  const pressureFormat = useSelector(selectUserPressureFormat);
  const pressureToUnit = 'pounds_per_square_inch';

  const onFinishIrrigation = (fieldValues: any, isEdit?: boolean, setLoading?: any) => {
    const {
      irrigation, irrigationPressure, irrigation_pressure_high,
      irrigation_pressure_low, depth, irrigation_max_valve_opening_duration
    } = fieldValues;

    const actionType = 'irrigation';
    const { config } = getThresholdConfig(mainThresholds, ranchId, actionType);
    const { id } = config || {};

    const {value: covertedDepth } = getUserUnits(Number(depth),
      depthFormat, undefined, {}, 'inches');

    const {value: pressureHigh } = getUserUnits(Number(irrigation_pressure_high),
      pressureFormat, undefined, {}, pressureToUnit);
    const {value: pressureLow } = getUserUnits(Number(irrigation_pressure_low),
      pressureFormat, undefined, {}, pressureToUnit);

    const finalObj = {
      ...(isEdit ? { id } : {}),
      action_type: actionType,
      action_subtype: 'pressure_irrigation',
      main_threshold: {
        depth: Math.round(covertedDepth),
        pressure_low_threshold: Number(pressureLow),
        pressure_high_threshold: Number(pressureHigh),
        smp_reference_sensor_id: irrigation?.reference_sensor_id,
        smp_reference_sensor_type: irrigation?.reference_sensor_type,
        pressure_reference_sensor_id: irrigationPressure?.reference_sensor_id,
        pressure_reference_sensor_type: irrigationPressure?.reference_sensor_type,
        smp_reference_sensor_identifier: irrigation?.reference_sensor_identifier,
        pressure_reference_sensor_identifier: irrigationPressure?.reference_sensor_identifier,
        max_valve_opening_duration: Number(irrigation_max_valve_opening_duration),
      },
      ranch: ranchId,
    };
    getThresholdAPIHandler(finalObj, isEdit, getNoteficationMessageStr(isEdit), setLoading, t);
  };

  const onFinishSprinker = (fieldValues: any, isEdit?: boolean, setLoading?: any) => {
    const {
      sprinklerPressure, sprinkler_pressure_high,
      sprinkler_pressure_low, sprinkler_max_valve_opening_duration,
      sprinklerDepth, sprinkler,
    } = fieldValues;
    const {value: covertedDepth } = getUserUnits(Number(sprinklerDepth),
      depthFormat, undefined, {}, 'inches');

    const {value: pressureHigh } = getUserUnits(Number(sprinkler_pressure_high),
      pressureFormat, undefined, {}, pressureToUnit);
    const {value: pressureLow } = getUserUnits(Number(sprinkler_pressure_low),
      pressureFormat, undefined, {}, pressureToUnit);

    const actionType = 'sprinkler';
    const { config } = getThresholdConfig(mainThresholds, ranchId, actionType);
    const { id } = config || {};

    const finalObj = {
      ...(isEdit ? { id } : {}),
      action_type: actionType,
      action_subtype: 'pressure_sprinkler',
      main_threshold: {
        depth: Math.round(covertedDepth),
        smp_reference_sensor_id: sprinkler?.reference_sensor_id,
        smp_reference_sensor_type: sprinkler?.reference_sensor_type,
        smp_reference_sensor_identifier: sprinkler?.reference_sensor_identifier,
        pressure_low_threshold: Number(pressureLow),
        pressure_high_threshold: Number(pressureHigh),
        pressure_reference_sensor_id: sprinklerPressure?.reference_sensor_id,
        pressure_reference_sensor_type: sprinklerPressure?.reference_sensor_type,
        pressure_reference_sensor_identifier: sprinklerPressure?.reference_sensor_identifier,
        max_valve_opening_duration: Number(sprinkler_max_valve_opening_duration),
      },
      ranch: ranchId
    };
    getThresholdAPIHandler(finalObj, isEdit, getNoteficationMessageStr(isEdit), setLoading, t);
  };

  return { onFinishIrrigation, onFinishSprinker };
};

export const getCustomEndDateValidation = (prefix: string = '', t) => {
  const requiredCustomEndDateValidation = [
    { required: true, message: t('Please select an end date!') },
    ({ getFieldValue }) => ({
      validator(_, value) {
        // Update the depending field value below if needed
        const startDate = getFieldValue(`${prefix}starting_time_window`);
        if (!value || !startDate || value?.isAfter(startDate)) {
          return Promise.resolve();
        }
        return Promise.reject(
          new Error(t('End date must be after the start date!'))
        );
      },
    }),
  ];

  return { requiredCustomEndDateValidation }
}

export const checkIsValidDate = (inputSdate: any, inputEdate: any,sDate: any, eDate: any, t:any ) => {
  if (!sDate || !eDate) {
    return { isValid: false, message: t('Date range is missing.') };
  }

  const isStartValid = moment(inputSdate).isSameOrAfter(sDate) && moment(inputSdate).isSameOrBefore(eDate);
  const isEndValid = moment(inputEdate).isSameOrAfter(sDate) && moment(inputEdate).isSameOrBefore(eDate);

  if (!isStartValid) {
    return { isValid: false, message: t('Please select a start of destination Time-Window within the source Time-Window.') };
  }

  if (!isEndValid) {
    return { isValid: false, message: t('Please select an end of destination Time-Window within the source Time-Window.') };
  }

  return { isValid: true, message: '' };
}

function getOverlappingRules(rules: any[], sMoment: any, eMoment: any) {
  const overlapping = rules?.filter((item) => {
    const itemStart = moment(item?.schedule?.starting_time_window);
    const itemEnd = moment(item?.schedule?.ending_time_window);

    if (!itemStart.isValid() || !itemEnd.isValid()) return false;
    // Check if existing startTime conflict with selected date range
    const startConflict =
      itemStart.isBetween(sMoment, eMoment) ||
      itemStart.isSame(sMoment) ||
      itemStart.isSame(eMoment);

    // Check if existing endTime conflict with selected date range
    const endConflict =
      itemEnd.isBetween(sMoment, eMoment) ||
      itemEnd.isSame(sMoment) ||
      itemEnd.isSame(eMoment);
    
    // Check if startTime and endTime overlaps 
    const overlaps = itemStart.isBefore(eMoment) && itemEnd.isAfter(sMoment);
    if (startConflict || endConflict || overlaps) {
      return true;
    }
    return false;
  });

  return overlapping
}

export function checkIsRuleExists(form: any, showWarning: boolean, configList: any[],
  isEdit: any, setWarningMessage: any, setShowWarning: any) {
  const {
    action_type,
    block,
    starting_time_window: sTime,
    ending_time_window: eTime,
    schedule
  } = form.getFieldsValue();
  const { id } = schedule || {}

  const subType = autonomousTypes[action_type]?.subType;
  if (!sTime || !eTime) return false;

  const sMoment = moment(sTime);
  const eMoment = moment(eTime);

  const existingRules = configList.filter((item: any) => (item.block_id === block &&
    item.action_subtype === subType));

  const filteredList = configList.filter((item: any) => {
    const itemStart = moment(item?.schedule?.starting_time_window);
    const itemEnd = moment(item?.schedule?.ending_time_window);

    if (!itemStart.isValid() || !itemEnd.isValid()) return false;

    const blockMatch = item.block_id === block &&
                       item.action_subtype === subType;

    // Check if existing startTime conflict with selected date range
    const startConflict =
      itemStart.isBetween(sMoment, eMoment) ||
      itemStart.isSame(sMoment) ||
      itemStart.isSame(eMoment);

    // Check if existing endTime conflict with selected date range
    const endConflict =
      itemEnd.isBetween(sMoment, eMoment) ||
      itemEnd.isSame(sMoment) ||
      itemEnd.isSame(eMoment);
    
    // Check if startTime and endTime overlaps 
    const overlaps = itemStart.isBefore(eMoment) && itemEnd.isAfter(sMoment);

    // Allow editing existing rule always
    if (isEdit && blockMatch && id) {
      const otherRules = existingRules?.filter((ex) => ex?.schedule?.id !== id);
      if (!otherRules.length) return false;
      else if (startConflict || endConflict || overlaps) {
        const overlappingRules = getOverlappingRules(otherRules, sMoment, eMoment);
        if (overlappingRules?.length) {
          setWarningMessage('Existing time window conflicts with selected time');
          return true;
        }
        return false;
      }
      return false;
    }
  
    if (blockMatch && !isEdit && (startConflict || endConflict || overlaps)) {
      setWarningMessage('Existing time window conflicts with selected time');
      return true;
    }

    return false;
  });

  const isShowWarning = !!filteredList.length;
  if (!isEqual(showWarning, isShowWarning)) setShowWarning(isShowWarning);

  return isShowWarning;
}
